/**
 * Die globale Kopfleiste klappt am Tablet beim Herunterscrollen ein (Rafael, 03.10.2026,
 * 09:26: „Ja" auf „Am Tablet würde ich die globale Kopfleiste zusätzlich beim Scrollen
 * einklappen lassen (ein Tipp oben holt sie zurück)").
 *
 * Nur bei Touch oder schmaler Breite — am Desktop bleibt die Leiste immer stehen.
 * Gehoert wird auf JEDES Scrollen im Dokument (Capture-Phase, `scroll` blubbert nicht):
 * die Guide-Spalte, ein Panel, die Seite selbst. Runter -> einklappen, hoch -> wieder da.
 * Scrollen in der Leiste selbst oder in ihren Menues zaehlt nicht.
 */
import { useEffect, useState } from 'react';

export const KOPF_EINKLAPP_MEDIEN = '(pointer: coarse), (max-width: 1099px)';
/** Mindestweg in px, bevor eine Richtung zaehlt — kleine Zitterer und Layoutspruenge nicht. */
export const KOPF_SCHWELLE = 12;
/** Unterhalb dieser Scrollhoehe bleibt (bzw. kommt) die Leiste immer. */
export const KOPF_OBEN = 24;

/** Reine Entscheidung je Scrollschritt; null = nichts aendern. */
export function kopfRichtung(vorher: number, jetzt: number): 'ein' | 'aus' | null {
  if (jetzt <= KOPF_OBEN) return 'aus';
  const d = jetzt - vorher;
  if (d >= KOPF_SCHWELLE) return 'ein';
  if (d <= -KOPF_SCHWELLE) return 'aus';
  return null;
}

const AUSGENOMMEN = '.cui-project-header, .cui-kopf-menue, .cui-view-popup';
/**
 * Nur Scrollen, das der Mensch selbst ausloest, zaehlt. Ohne diese Grenze klappte die
 * Leiste schon beim Oeffnen einer Buehne ein (die Spalte springt per Programm an den
 * Anfang bzw. an eine Frage) — gemessen in der Vorschau am 03.10.2026.
 */
const GESTE_MS = 1200;

export function useKopfEinklappen(): [boolean, (v: boolean) => void] {
  const [aktiv, setAktiv] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(KOPF_EINKLAPP_MEDIEN).matches);
  const [eingeklappt, setEingeklappt] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.(KOPF_EINKLAPP_MEDIEN);
    if (!mq) return;
    const neu = () => setAktiv(mq.matches);
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);
  useEffect(() => {
    if (!aktiv) { setEingeklappt(false); return; }
    const stand = new WeakMap<object, number>();
    let ruheBis = 0;
    let letzteGeste = -Infinity;
    // Kein pointerdown/Tipp: ein Tipp auf eine Karte oeffnet eine Buehne, und deren
    // Sprung per Programm darf die Leiste nicht wegklappen. Tasten nur, wenn sie scrollen.
    const SCROLLTASTEN = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ']);
    const geste = (e: Event) => {
      if (e.type === 'keydown' && !SCROLLTASTEN.has((e as KeyboardEvent).key)) return;
      letzteGeste = performance.now();
    };
    const GESTEN = ['touchmove', 'wheel', 'keydown'] as const;
    const beimScrollen = (e: Event) => {
      const ziel = (e.target as Node)?.nodeType === 9 ? ((e.target as Document).scrollingElement ?? (e.target as Document).documentElement) : e.target;
      // Elemente aus einem iframe sind nicht `instanceof Element` dieses Fensters — daher nodeType.
      if (!ziel || (ziel as Node).nodeType !== 1) return;
      const el = ziel as Element;
      if (el.closest(AUSGENOMMEN)) return;
      const jetzt = el.scrollTop;
      const vorher = stand.get(el);
      if (performance.now() - letzteGeste > GESTE_MS) { stand.set(el, jetzt); return; }
      if (vorher === undefined) { stand.set(el, jetzt); return; }
      if (performance.now() < ruheBis) { stand.set(el, jetzt); return; }
      const r = kopfRichtung(vorher, jetzt);
      if (r === null) return; // Weg sammeln, bis die Schwelle erreicht ist
      stand.set(el, jetzt);
      setEingeklappt(alt => {
        const soll = r === 'ein';
        // Das Ein-/Ausklappen verschiebt das Layout; die Folge-Scrollereignisse daraus nicht werten.
        if (alt !== soll) ruheBis = performance.now() + 300;
        return soll;
      });
    };
    /*
     * AUCH IN EINGEBETTETEN SEITEN. Die Entscheidungsseite einer Buehne und viele Panels
     * sind iframes — ihr Scrollen erreicht dieses Dokument nicht. Gleichherkunft-Frames
     * bekommen deshalb dieselben Horcher, sobald sie laden (`load` blubbert nicht, kommt
     * aber in der Capture-Phase hier an). Fremde Herkunft wirft beim Zugriff: dort klappt
     * die Leiste eben nicht von selbst ein — der Streifen bleibt der Weg.
     */
    const dokumente = new Set<Document>();
    const haenge = (doc: Document) => {
      if (dokumente.has(doc)) return;
      dokumente.add(doc);
      doc.addEventListener('scroll', beimScrollen, { capture: true, passive: true });
      for (const g of GESTEN) doc.addEventListener(g, geste, { capture: true, passive: true });
    };
    const frame = (f: HTMLIFrameElement) => { try { if (f.contentDocument) haenge(f.contentDocument); } catch { /* fremde Herkunft */ } };
    const beimLaden = (e: Event) => { if (e.target instanceof HTMLIFrameElement) frame(e.target); };
    haenge(document);
    document.querySelectorAll('iframe').forEach(frame);
    document.addEventListener('load', beimLaden, { capture: true });
    return () => {
      document.removeEventListener('load', beimLaden, { capture: true });
      for (const doc of dokumente) {
        doc.removeEventListener('scroll', beimScrollen, { capture: true });
        for (const g of GESTEN) doc.removeEventListener(g, geste, { capture: true });
      }
    };
  }, [aktiv]);
  return [aktiv && eingeklappt, setEingeklappt];
}
